import {
  getCookieFromDocument,
  setCookieForRootDomain,
} from '@iheartjane/utils';
import { useEffect } from 'react';

import {
  JDID_KEY,
  TWO_YEARS_IN_SECONDS,
  isValidJdid,
} from '@jane/bloom/utils/jdid';
import { logError } from '@jane/bloom/utils/logError';

/**
 * Ensures the server JDID is persisted in the root domain cookie.
 *
 * This is called on client hydration to ensure the JDID cookie is set.
 * The cookie is set on the root domain so it's accessible to both the
 * embedded menu row component and the Bloom menu when they're on the
 * same domain or subdomains of the same root domain.
 */
const ensureJdidCookie = (serverJdid: string): void => {
  if (typeof window === 'undefined') return;
  if (!isValidJdid(serverJdid)) return;

  try {
    const currentCookieJdid = getCookieFromDocument(JDID_KEY);

    if (currentCookieJdid !== serverJdid) {
      setCookieForRootDomain(JDID_KEY, serverJdid, TWO_YEARS_IN_SECONDS);
    }
  } catch (error) {
    logError(error, { context: 'jdid-cookie-sync' });
  }
};

/**
 * Hook that ensures the server JDID is persisted in the root domain cookie on
 * client hydration.
 *
 * Tracking registration (Mixpanel and Datadog RUM) happens earlier, in
 * `entry.client.tsx`, keyed to the same server JDID via `window.__BLOOM_CLIENT_ENV__`.
 * @param serverJdid - The JDID provided by the server (may be from cookie or newly generated)
 */
export const useJdidSync = (serverJdid: string): void => {
  useEffect(() => {
    ensureJdidCookie(serverJdid);
  }, [serverJdid]);
};
